import { useState, useEffect, useRef, useCallback } from 'react';
import { generateContentWithCache } from '../lib/geminiService';
import { parseAIJSON } from '../utils/aiUtils';
import { BRIDGE_EVENTS } from '../modules/erp/utils/commandBridge';

export interface ProcessedItem {
  id: string;
  name: string;
  category: 'fixo' | 'variável';
  itemType?: 'insumo' | 'produto_venda' | 'custo_fixo';
  salePrice?: number;
  unit: string;
  price: number;
  quantity: number;
  isAsset: boolean;
  depreciationMonths?: number;
}

export interface UseCostCenterAIProps {
  userId: string;
  onConfirmItem: (item: Omit<ProcessedItem, 'id'>) => Promise<void>;
}

interface RawAIAssistantItem {
  name?: unknown;
  category?: unknown;
  itemType?: unknown;
  salePrice?: unknown;
  unit?: unknown;
  price?: unknown;
  quantity?: unknown;
  isAsset?: unknown;
  depreciationMonths?: unknown;
}

export function useCostCenterAI({ userId, onConfirmItem }: UseCostCenterAIProps) {
  const [inputValue, setInputValue] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  
  const storageKey = `r-max:costcenter:${userId}:pending-items`;

  const [pendingItems, setPendingItems] = useState<ProcessedItem[]>(() => {
    try {
      const stored = sessionStorage.getItem(storageKey);
      return stored ? JSON.parse(stored) as ProcessedItem[] : [];
    } catch {
      return [];
    }
  });
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef<boolean>(true);

  const cancelExtraction = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    isProcessingRef.current = false;
    if (isMountedRef.current) {
      setIsProcessing(false);
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      cancelExtraction();
    };
  }, [cancelExtraction]);

  // Persistir fila no sessionStorage e ref síncrono a cada alteração
  const pendingItemsRef = useRef(pendingItems);
  useEffect(() => {
    pendingItemsRef.current = pendingItems;
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(pendingItems));
    } catch (err) {
      console.error('Erro ao salvar no sessionStorage:', err);
    }
  }, [pendingItems, storageKey]);

  // Ponte Omnibar -> Centro de Custos: recarrega a fila quando itens são enviados pelo comando
  useEffect(() => {
    const handleStaged = () => {
      try {
        const stored = sessionStorage.getItem(storageKey);
        const parsed = stored ? JSON.parse(stored) as ProcessedItem[] : [];
        if (Array.isArray(parsed)) setPendingItems(parsed);
      } catch (err) {
        console.error('Erro ao ler itens enviados pelo comando:', err);
      }
    };
    window.addEventListener(BRIDGE_EVENTS.costs, handleStaged);
    return () => window.removeEventListener(BRIDGE_EVENTS.costs, handleStaged);
  }, [storageKey]);

  const isProcessingRef = useRef(false);

  const processWithAI = useCallback(async (text: string): Promise<void> => {
    if (!text.trim()) {
      setStatusMessage('Por favor, introduza uma mensagem de texto.');
      return;
    }

    if (isProcessingRef.current) return;
    isProcessingRef.current = true;
    setIsProcessing(true);
    setStatusMessage('Analisando e organizando os dados...');

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    const systemInstruction = `Você é um extrator especialista em gestão de custos do sistema R-MAX.
Aja como assistente de contabilidade e extraia todos os itens de custo e insumos que o usuário digitou no texto.
Retorne obrigatoriamente APENAS um objeto JSON válido (com a propriedade "items") sem qualquer texto conversacional antes ou depois.
Regras de classificação:
1. categoria: use "variável" para insumos de consumo (matéria-prima, embalagem, gás, ingredientes) e "fixo" para custos recorrentes/infraestrutura/ferramentas.
2. isAsset: use true se for um equipamento ou ativo durável (batedeira, geladeira, furadeira). Caso contrário, use false.
3. depreciationMonths: se isAsset for true, defina a vida útil estimada em meses (ex: 12, 24, 36). Caso contrário, omita.
4. price: preço total pago pelo item.
5. quantity: quantidade total adquirida.`;

    const promptText = `${systemInstruction}\n\nTexto do usuário:\n"${text}"`;

    try {
      const response = await generateContentWithCache({
        model: 'gemini-3.5-flash-lite',
        contents: [
          {
            parts: [{ text: promptText }]
          }
        ],
        config: {
          generationConfig: {
            responseMimeType: 'application/json'
          }
        },
        signal: abortControllerRef.current?.signal ?? undefined,
        skipCache: true
      });

      const rawText = response.text;

      if (!rawText) {
        throw new Error('A resposta veio vazia.');
      }

      // Parser resiliente de JSON usando aiUtils
      const parsedJson = parseAIJSON<any>(rawText, null);

      if (!parsedJson) {
        throw new Error('Não foi possível organizar os dados enviados.');
      }

      let rawItems: RawAIAssistantItem[] = [];
      if (Array.isArray(parsedJson)) {
        rawItems = parsedJson;
      } else if (typeof parsedJson === 'object' && Array.isArray(parsedJson.items)) {
        rawItems = parsedJson.items;
      } else {
        throw new Error('O formato retornado não contém a lista de itens esperada.');
      }

      const extractedItems = rawItems.map((item): ProcessedItem => {
        const name = typeof item.name === 'string' && item.name.trim() ? item.name : 'Item Sem Nome';
        const category = (item.category === 'fixo' || item.category === 'variável') ? item.category : 'variável';
        const unit = typeof item.unit === 'string' ? item.unit : 'un';
        const price = typeof item.price === 'number' ? item.price : 0;
        const quantity = typeof item.quantity === 'number' ? item.quantity : 1;
        const isAsset = typeof item.isAsset === 'boolean' ? item.isAsset : false;
        const depreciationMonths = typeof item.depreciationMonths === 'number' ? item.depreciationMonths : undefined;

        return {
          id: crypto.randomUUID(),
          name,
          category,
          unit,
          price,
          quantity,
          isAsset,
          depreciationMonths
        };
      });

      if (extractedItems.length === 0) {
        setStatusMessage('⚠️ Nenhum item válido pôde ser extraído do texto fornecido.');
      } else {
        setPendingItems((prev) => [...prev, ...extractedItems]);
        setInputValue('');
        setStatusMessage('✓ Extração concluída com sucesso!');
      }
      setTimeout(() => setStatusMessage(null), 5000);
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        return;
      }
      const msg = err instanceof Error ? err.message : 'Falha na conexão com o serviço';
      if (msg.includes('cota') || msg.includes('depleted') || msg.includes('429')) {
        setStatusMessage('⚠️ Leitura automática temporariamente ocupada. Por favor, utilize o botão "+ Cadastrar Manualmente" abaixo.');
      } else {
        setStatusMessage(`⚠️ ${msg}`);
      }
      console.error('useCostCenterAI error:', err);
    } finally {
      isProcessingRef.current = false;
      if (!abortControllerRef.current?.signal.aborted) {
        setIsProcessing(false);
      }
    }
  }, [storageKey]);

  const updatePendingItem = useCallback((id: string, updates: Partial<ProcessedItem>): void => {
    setPendingItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...updates } : item))
    );
  }, []);

  const discardItem = useCallback((id: string): void => {
    setPendingItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const confirmItem = useCallback(async (id: string): Promise<void> => {
    const itemToConfirm = pendingItemsRef.current.find((item) => item.id === id) || pendingItems.find((item) => item.id === id);
    if (!itemToConfirm) return;

    setStatusMessage(`A guardar item "${itemToConfirm.name}"...`);
    try {
      // Cria uma cópia sem o ID temporário do hook
      const { id: _, ...payload } = itemToConfirm;
      await onConfirmItem(payload);
      
      // Remove da fila apenas se a promessa foi resolvida com sucesso
      setPendingItems((prev) => prev.filter((item) => item.id !== id));
      setStatusMessage(null);
    } catch (err: unknown) {
      if (itemToConfirm) {
        const msg = err instanceof Error ? err.message : 'Erro desconhecido';
        setStatusMessage(`Falha ao guardar item "${itemToConfirm.name}": ${msg}`);
      }
      console.error('confirmItem error:', err);
      throw err;
    }
  }, [onConfirmItem, pendingItems]);

  return {
    inputValue,
    setInputValue,
    isProcessing,
    cancelExtraction,
    pendingItems,
    setPendingItems,
    statusMessage,
    setStatusMessage,
    processWithAI,
    updatePendingItem,
    discardItem,
    confirmItem
  };
}
